import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { BG_IMAGE_ALIGNMENT_OPTIONS, BgImageAlignmentOptions, Icon } from '#const';
import { getIconUrl } from '#utils';
import { createElement } from 'react';
import FormSkeleton from './FormSkeleton';
import { DEFAULT_FORM_HEIGHT } from './constants';

const Form = dynamic(() => import('./Form'), {
	ssr: false,
	loading: () => createElement(FormSkeleton),
});

const defaultFinePrint = `<p>
				By clicking &apos;Do I Qualify&apos; and submitting my request,
				I provide my electronic signature and consent to receive phone calls
				and/or text messages from Total Injury Help to the above telephone
				number containing offers to identify a lawyer in its network that may
				assist me. These communications may be generated using an autodialer,
				and/or pre-recorded or artificial voice, and that consent is not required
				to utilize Total Injury Help services. This authorization overrides any
				previous registrations on a federal or state Do Not Call registry. I agree
				to the <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer">privacy policy</a>
				and <a href="/terms-of-use/" target="_blank" rel="noopener noreferrer">terms</a>
				(including mandatory arbitration and class waiver).
				</p>`;

export const defaultOptions = {
	heading: true,
	color: 'rgba(255,219,0, 1)',
	headingCopy: `<h2>Find Out if You Qualify</h2>
				<br/> <h3><u>SIGNIFICANT</u> Compensation May Be Available</h3>`,
	form: {
		'@type': '@builder.io/core:Reference',
		id: 'f86c3b6d48c94d8da964dd705d15438c',
		model: 'form',
		value: { data: { name: 'Form', formId: 'f86c3b6d48c94d8da964dd705d15438c' } },
	},
	showFinePrint: true,
	showLogos: true,
	showDynamicPhoneNumber: false,
	finePrint: defaultFinePrint,
	centered: false,
	maxWidth: 425,
	intakeFormId: '',
	borderWidth: 3,
	formHeight: DEFAULT_FORM_HEIGHT,
} as const;

Builder.registerComponent(Form, {
	name: 'Form',
	inputs: [
		// --- Heading ---
		{
			name: 'heading',
			friendlyName: 'Show Heading',
			type: 'boolean',
			defaultValue: defaultOptions.heading,
			helperText: 'Show the heading above the form',
		},
		{
			name: 'headingCopy',
			friendlyName: 'Heading',
			type: 'richText',
			defaultValue: defaultOptions.headingCopy,
			showIf: function (options) {
				return options.get('heading') === true;
			},
		},
		{
			name: 'headingColor',
			friendlyName: 'Heading Background Color',
			type: 'color',
			defaultValue: defaultOptions.color,
			helperText:
				'Background color of the heading box. Set to transparent to show heading on white.',
			showIf: function (options) {
				return options.get('heading') === true;
			},
		},
		{
			name: 'headingTitleSize',
			friendlyName: 'Heading Title Size',
			type: 'object',
			helperText: 'Font size for h1/h2 in the heading. Defaults to 22px.',
			showIf: function (options) {
				return options.get('heading') === true;
			},
			subFields: [
				{
					name: 'desktop',
					friendlyName: 'Desktop (px)',
					type: 'number',
				},
				{
					name: 'mobile',
					friendlyName: 'Mobile (px)',
					type: 'number',
					helperText: 'Falls back to desktop value if not set.',
				},
			],
		},
		{
			name: 'headingSubtitleSize',
			friendlyName: 'Heading Subtitle Size',
			type: 'object',
			helperText: 'Font size for h3/h4 in the heading. Defaults to 18px.',
			showIf: function (options) {
				return options.get('heading') === true;
			},
			subFields: [
				{
					name: 'desktop',
					friendlyName: 'Desktop (px)',
					type: 'number',
				},
				{
					name: 'mobile',
					friendlyName: 'Mobile (px)',
					type: 'number',
					helperText: 'Falls back to desktop value if not set.',
				},
			],
		},
		// --- Border ---
		{
			name: 'border',
			friendlyName: 'Border',
			type: 'object',
			showIf: function (options) {
				return options.get('heading') === true;
			},
			subFields: [
				{
					name: 'color',
					friendlyName: 'Color',
					type: 'color',
					defaultValue: defaultOptions.color,
					helperText: 'Color of the border around the form container.',
				},
				{
					name: 'width',
					friendlyName: 'Width (px)',
					type: 'number',
					defaultValue: defaultOptions.borderWidth,
					helperText: 'Thickness of the border in pixels (default: 3px).',
				},
			],
		},
		// --- Form content ---
		{
			name: 'form',
			friendlyName: 'Form',
			type: 'reference',
			model: 'form',
			defaultValue: {
				'@type': '@builder.io/core:Reference',
				id: defaultOptions.form.id,
				model: 'form',
			},
		},
		{
			name: 'showFinePrint',
			friendlyName: 'Show TCPA',
			type: 'boolean',
			defaultValue: defaultOptions.showFinePrint,
			helperText: `If true will show TCPA. TCPA is REQUIRED for any forms.
			This option allows it to be removed from the first step`,
		},
		{
			name: 'finePrint',
			friendlyName: 'Fine Print',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.finePrint,
		},
		{
			name: 'showLogos',
			friendlyName: 'Show Logos',
			type: 'boolean',
			defaultValue: defaultOptions.showLogos,
			helperText: 'If TCPA text is shown this is an option to show or hide logos',
		},
		{
			name: 'showDynamicPhoneNumber',
			friendlyName: 'Show Dynamic Phone Number',
			type: 'boolean',
			defaultValue: defaultOptions.showDynamicPhoneNumber,
			helperText: 'If true will query for a dynamic phone number',
		},
		// --- Layout ---
		{
			name: 'centered',
			friendlyName: 'Center Form',
			type: 'boolean',
			defaultValue: defaultOptions.centered,
			helperText: 'Center the form container on the page',
		},
		{
			name: 'maxWidth',
			friendlyName: 'Form Max Width - Desktop (px)',
			type: 'number',
			defaultValue: defaultOptions.maxWidth,
			helperText: 'Max width of the form container on desktop in pixels (default: 425px)',
		},
		{
			name: 'formHeight',
			friendlyName: 'Form Height (px)',
			type: 'number',
			defaultValue: defaultOptions.formHeight,
			helperText: `Minimum height of the form container in pixels (default: ${defaultOptions.formHeight}px). Reserves space to prevent layout shift while the form loads.`,
		},
		{
			name: 'maxWidthMobile',
			friendlyName: 'Form Max Width - Mobile (px)',
			type: 'number',
			helperText:
				'Form width (px) at a 400px reference; scales with the container through 1280px, then desktop max only. Leave empty for default.',
		},
		{
			name: 'formPadding',
			friendlyName: 'Form Horizontal Padding - Desktop Only (px)',
			type: 'number',
			helperText:
				'Horizontal padding around the form inputs on desktop only. Automatically removed on mobile so inputs stay full width.',
		},
		{
			name: 'paddingBlockStart',
			friendlyName: 'Top spacing above form — mobile only (px)',
			type: 'number',
			helperText:
				'Optional padding above the form block on viewports ≤767px only (outside the bordered card). Leave empty for no extra space.',
		},
		// --- Background ---
		{
			name: 'backgroundImage',
			friendlyName: 'Background Image',
			type: 'object',
			required: false,
			helperText: 'Optional image placed in the background.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'backgroundImagePlacement',
					friendlyName: 'Background Image Placement Bias',
					type: 'string',
					enum: [...BG_IMAGE_ALIGNMENT_OPTIONS],
					defaultValue: BgImageAlignmentOptions.CENTER,
					helperText: `How the background Image will attach to the background of the container.
						Use it to move the focal point of the image if needed.`,
				},
			],
		},
		{
			name: 'backgroundColor',
			friendlyName: 'Background Color',
			type: 'color',
			helperText: 'Background color behind the form, shown if no background image is provided',
		},
		// --- Advanced ---
		{
			name: 'intakeFormId',
			friendlyName: 'Intake Form ID',
			type: 'string',
			defaultValue: '',
			helperText: `The form ID of the intake form to show after the form is submitted
			and the user qualifies for an internal intake`,
		},
	],
	canHaveChildren: true,
	defaultChildren: [
		{
			'@type': '@builder.io/sdk:Element',
			component: {
				name: 'BragBar',
				options: {},
			},
		},
	],
	image: getIconUrl(Icon.FORM),
});
